import React, { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TbClockHour3 } from "react-icons/tb";

import notify from "../common/notify";
import { Oval } from "react-loader-spinner";
import UserPlaceHolderImg from "../../assets/img/common/user-placeholder-img.png";
import { HiOutlineChevronDown } from "react-icons/hi";
import * as Scroll from "react-scroll";
import ReactTimeAgo from "react-time-ago";
import { handleError } from "../../hooks/useApi";
import useNavigateToProfile from "../../hooks/useNavigateToProfile";
import { MdOutlineModeEdit, MdOutlineReply } from "react-icons/md";
import { publicAddress } from "../../utils/localData";
import { BsTrash } from "react-icons/bs";
import { selectGeneral, setShowAuthModal } from "../../redux/slices/generalSlice";

// Component for displaying and interacting with comments

const CommentBox = ({
  data, // Comment data including content, author, and other details
  useDeleteCommentMutation, // Mutation hook for deleting comments
  useAddCommentMutation, // Mutation hook for adding comments
  useEditCommentMutation, // Mutation hook for editing comments
  showReplies, // Flag to display/hide replies
  setShowReplies, // Function to control the visibility of replies
  parentId, // ID of the parent comment (if it's a reply)
  refetch, // Function to refetch comment data
}) => {
  const {
    replies,
    _id: id,
    body,
    publishedAt,
    project,
    result,
    user,
    isReply,
  } = data ?? {};

  // State variables for managing comment interactions
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [crudLoading, setCrudLoading] = useState(false);
  const [enableEditMode, setEnableEditMode] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [editBody, setEditBody] = useState("");

  // Mutation hooks for adding, editing, and deleting comments
  const [addComment, { isLoading: addCommentLoading }] =
    useAddCommentMutation();
  const [editComment, { isLoading: editCommentLoading }] =
    useEditCommentMutation();
  const [deleteComment, { isLoading: deleteCommentLoading }] =
    useDeleteCommentMutation();

  // Memoized value for refetching based on project or result
  const refetchId = useMemo(() => project ?? result, [project, result]);

  // Use simple login check - no SIWE signature needed for comments
  const dispatch = useDispatch();
  const { isLoggedIn } = useSelector(selectGeneral);

  // Function to handle the submission of a comment
  const handleReplySubmit = async () => {
    // Just need to be logged in - no SIWE signature required for comments
    if (!isLoggedIn) {
      dispatch(setShowAuthModal({ show: true }));
      return;
    }
    
    setCrudLoading(true);
    try {
      const res = await addComment({
        id: refetchId,
        body: {
          body: replyBody,
          replyTo: isReply ? parentId : id,
        },
      }).unwrap();
      await refetch({ id: refetchId }).unwrap();
      notify(res?.message, "success");
      setShowReplyInput(false);
      setReplyBody("");
    } catch (error) {
      handleError(error, null, true);
    }
    setCrudLoading(false);
  };

  // Function to cancel the reply
  const handleCancelReply = () => {
    setShowReplyInput(false);
    setReplyBody("");
  };

  // Ref for the reply input field
  const replyInputRef = useRef(null);

  // Function to focus on the reply input field
  const handleReply = () => {
    setShowReplyInput(true);
    Scroll.scroller.scrollTo("replyInput" + id, {
      duration: 1000,
      smooth: "easeInOutQuint",
      offset: -330,
    });
    replyInputRef.current.focus();
  };

  // Function to delete the comment
  const handleDelete = async () => {
    // Just need to be logged in - no SIWE signature required
    if (!isLoggedIn) {
      dispatch(setShowAuthModal({ show: true }));
      return;
    }
    
    setCrudLoading(true);
    try {
      const res = await deleteComment({
        id,
      }).unwrap();
      await refetch({ id: refetchId }).unwrap();
      notify(res?.message, "success");
    } catch (error) {
      handleError(error, null, true);
    }
    setCrudLoading(false);
  };

  // Function to edit the comment
  const handleEdit = async () => {
    // Just need to be logged in - no SIWE signature required
    if (!isLoggedIn) {
      dispatch(setShowAuthModal({ show: true }));
      return;
    }
    
    setCrudLoading(true);
    try {
      const res = await editComment({
        id,
        body: {
          body: editBody,
        },
      }).unwrap();
      await refetch({ id: refetchId }).unwrap();
      setEnableEditMode(false);
      notify(res?.message, "success");
    } catch (error) {
      handleError(error, null, true);
    }
    setCrudLoading(false);
  };

  // Custom hook for navigating to user profiles
  const { navigateToProfile } = useNavigateToProfile();

  return (
    <div className="w-full flex flex-col items-end">
      <div
        className={`w-full bg-gray-50/80 rounded-2xl flex flex-col pt-5 pb-2 pr-4 shadow-sm`}
      >
        <div className="w-full flex items-center justify-between">
          <div
            className=" flex pl-4 cursor-pointer "
            onClick={() => navigateToProfile(user?._id, user?.role, false)}
          >
            <img
              src={user?.images?.profile || UserPlaceHolderImg}
              alt={""}
              className="h-12 w-12 rounded-full"
            />
            <div className="flex flex-col ml-3 ">
              <h1 className="text-dark text-lg font-bold ">
                {" "}
                {user?.fundername ?? `${user?.firstname} ${user?.lastname}`}
              </h1>
              <span className=" text-[#757575] truncate -mt-1 ">
                {user?.title_rank || user?.location || user?.email}
              </span>
            </div>
          </div>

          {user?.public_address === publicAddress() ? (
            <div className="flex items-center gap-3">
              <button
                disabled={crudLoading}
                onClick={handleDelete}
                className="text-red-500 flex items-center justify-end disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <BsTrash className="mr-1" />
                <span className="hidden sm:flex">Delete</span>
              </button>
              <button
                disabled={crudLoading}
                onClick={() => {
                  setEnableEditMode(true);
                  setEditBody(body);
                }}
                className="text-primary flex items-center justify-end disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <MdOutlineModeEdit className="mr-1" />
                <span className="hidden sm:flex">Edit</span>
              </button>
            </div>
          ) : (
            <button
              onClick={handleReply}
              className="text-primary flex items-center justify-end"
            >
              <MdOutlineReply className="mr-1" />
              <span className="hidden sm:flex">Reply</span>
            </button>
          )}
        </div>
        {enableEditMode ? (
          <div
            className={`w-full  flex-col py-3 px-3 items-center ${
              showReplyInput ? "show" : "hide"
            } `}
          >
            <textarea
              onChange={(e) => setEditBody(e.target.value)}
              value={editBody}
              placeholder={`Edit Your Comment...`}
              className="w-full outline-none h-20 resize-none rounded-xl px-3 py-3 bg-white shadow-sm focus:ring-2 focus:ring-primary/50 transition-all"
            ></textarea>
            <div className="w-full flex justify-end mt-3 ">
              <button
                type="button"
                onClick={() => setEnableEditMode(false)}
                disabled={crudLoading}
                className="w-24 h-8 mr-2 flex justify-center items-center bg-gray-100 hover:bg-gray-200 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleEdit}
                disabled={crudLoading}
                className="bg-primary transition-all duration-300 text-white flex items-center justify-center w-24 rounded-lg h-8 login-btn text-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                {editCommentLoading ? (
                  <Oval
                    height={23}
                    width={23}
                    color="#ffffff"
                    wrapperStyle={{}}
                    wrapperClass=""
                    visible={true}
                    ariaLabel="oval-loading1"
                    secondaryColor="#446CB3"
                    strokeWidth={2}
                    strokeWidthSecondary={2}
                  />
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        ) : (
          <p className=" text-[#2c2c2c] leading-7 whitespace-pre-wrap mt-2 pl-4 text-left w-full">
            {body}
          </p>
        )}

        <div className="w-full flex justify-between pt-5 pb-3 text-sm pl-3">
          <ReactTimeAgo
            date={publishedAt}
            locale="en-US"
            className="text-sm text-custom-gray-600 min-w-fit"
            timeStyle=""
          />
          {replies?.length > 0 && (
            <button
              onClick={() => setShowReplies((prev) => !prev)}
              className="text-primary flex items-center "
            >
              {showReplies ? "Hide Replies" : "Show Replies"} ({replies?.length}
              )
              <HiOutlineChevronDown
                className={`text-primary ml-1 transition-all duration-300 ${
                  showReplies ? "rotate-180" : "rotate-0"
                }`}
              />
            </button>
          )}
        </div>
        {/* ////////// */}
      </div>

      <div
        id={"replyInput" + id}
        className={`w-full  flex-col py-3 px-3 items-center reply-input  ${
          showReplyInput ? "show" : "hide"
        } `}
      >
        <textarea
          onChange={(e) => setReplyBody(e.target.value)}
          value={replyBody}
          placeholder={`Reply To ${
            user?.fundername ?? `${user?.firstname} ${user?.lastname}`
          } ...`}
          type="text"
          ref={replyInputRef}
          className="w-full outline-none h-20 resize-none rounded-xl px-3 py-3 bg-white shadow-sm focus:ring-2 focus:ring-primary/50 transition-all"
        ></textarea>
        <div className="w-full flex justify-end mt-3 ">
          <button
            type="button"
            onClick={handleCancelReply}
            disabled={crudLoading}
            className="w-24 h-8 mr-2 flex justify-center items-center bg-gray-100 hover:bg-gray-200 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 rounded-lg text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleReplySubmit}
            disabled={crudLoading}
            className="bg-primary transition-all duration-300 text-white flex items-center justify-center w-24 rounded-lg h-8 login-btn text-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {crudLoading ? (
              <Oval
                height={23}
                width={23}
                color="#ffffff"
                wrapperStyle={{}}
                wrapperClass=""
                visible={true}
                ariaLabel="oval-loading1"
                secondaryColor="#446CB3"
                strokeWidth={2}
                strokeWidthSecondary={2}
              />
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
const CommentItem = ({
  data,
  refetch,
  useDeleteCommentMutation,
  useAddCommentMutation,
  useEditCommentMutation,
}) => {
  const { replies, _id: id } = data ?? {};
  const [showReplies, setShowReplies] = useState(false);

  return (
    <div className="w-full flex flex-col items-end rounded-2xl overflow-hidden">
      <CommentBox
        data={data}
        setShowReplies={setShowReplies}
        showReplies={showReplies}
        useAddCommentMutation={useAddCommentMutation}
        useDeleteCommentMutation={useDeleteCommentMutation}
        useEditCommentMutation={useEditCommentMutation}
        parentId={id}
        refetch={refetch}
      />

      {showReplies && replies?.length > 0 && (
        <div className="w-full flex justify-end ">
          <div className="w-11/12 flex flex-col mt-3 gap-3">
            {replies?.map((item, index, arr) => (
              <CommentBox
                data={item}
                setShowReplies={setShowReplies}
                showReplies={showReplies}
                useAddCommentMutation={useAddCommentMutation}
                useDeleteCommentMutation={useDeleteCommentMutation}
                useEditCommentMutation={useEditCommentMutation}
                parentId={id}
                refetch={refetch}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentItem;
